import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Gift, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MarkSoldDialog = ({ product, onClose, onSold }) => {
  const [code, setCode] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/referrals`)
      .then(res => setReferrals(res.data))
      .catch(() => toast.error("No se pudieron cargar los codigos de referido"));
  }, []);

  const normalized = code.trim().toUpperCase();
  const match = referrals.find(r => r.code === normalized);
  const suggestions = normalized.length >= 2 && !match
    ? referrals.filter(r => r.code.includes(normalized.replace(/^OYB-?/, "")) || r.name.toUpperCase().includes(normalized)).slice(0, 5)
    : [];

  const confirm = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/products/${product.id}`, { sold: true, referral_code: normalized || null });
      toast.success(normalized ? `Vendido! +1 venta para ${normalized}` : "Vendido!");
      onSold();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[95] flex items-center justify-center p-4" onClick={onClose} data-testid="mark-sold-dialog">
      <div className="glass-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-oswald text-xl font-bold text-white uppercase">Marcar como vendido</h3>
            <p className="text-sm text-[#A0A0A0] mt-1 line-clamp-1">{product.name}</p>
          </div>
          <button onClick={onClose} className="text-[#A0A0A0] hover:text-white" data-testid="mark-sold-close"><X className="w-5 h-5" /></button>
        </div>

        <label className="block text-xs text-[#A0A0A0] uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Gift className="w-3.5 h-3.5 text-[#B026FF]" /> Codigo de referido (opcional)
        </label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="OYB-XXXX o nombre"
          className="w-full bg-[#050505] border border-white/10 text-white px-4 py-3 rounded-xl font-mono tracking-widest uppercase focus:border-[#B026FF] focus:outline-none placeholder-[#A0A0A0]/40 placeholder:font-sans placeholder:tracking-normal placeholder:normal-case"
          data-testid="mark-sold-referral-input"
          autoFocus
        />
        {match && (
          <p className="text-sm text-[#00FF66] mt-2 flex items-center gap-1" data-testid="mark-sold-referral-match">
            <Check className="w-4 h-4" /> {match.name} recibira $50
          </p>
        )}
        {!match && normalized && suggestions.length === 0 && normalized.length >= 4 && (
          <p className="text-sm text-amber-400 mt-2" data-testid="mark-sold-referral-nomatch">Ese codigo no existe. Deja vacio si no hubo referido.</p>
        )}
        {suggestions.length > 0 && (
          <div className="mt-2 space-y-1" data-testid="mark-sold-suggestions">
            {suggestions.map(r => (
              <button key={r.code} onClick={() => setCode(r.code)} className="w-full text-left bg-[#121212] hover:bg-[#1a1a1a] border border-white/5 rounded-lg px-3 py-2 text-sm flex justify-between" data-testid={`mark-sold-suggestion-${r.code}`}>
                <span className="text-white">{r.name}</span>
                <span className="font-mono text-[#B026FF]">{r.code}</span>
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-[#A0A0A0] mt-3">Busca "Referido por:" en el mensaje de WhatsApp del cliente.</p>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 border border-white/10 text-white py-3 rounded-xl font-semibold hover:border-white/30" data-testid="mark-sold-cancel">Cancelar</button>
          <button onClick={confirm} disabled={saving || (normalized && !match)} className="flex-1 bg-[#00FF66] text-[#050505] py-3 rounded-xl font-bold disabled:opacity-40" data-testid="mark-sold-confirm">
            {saving ? "..." : "Confirmar venta"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
